import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useJsApiLoader } from "@react-google-maps/api";

import DetailScaffold from "./components/DetailScaffold";
import Gallery from "./components/Gallery";
import OfferBuyBox from "./components/OfferBuyBox";
import ProductFiltersPanel, {
  SORT_OPTIONS,
  sortOptionId,
} from "./components/ProductFiltersPanel";
import ProductMapCard from "./components/ProductMapCard";
import SupplierDetailSheet from "./components/SupplierDetailSheet";
import SupplierRow from "./components/SupplierRow";
import TabStrip from "./components/TabStrip";
import { PinIcon } from "./components/DetailBits";
import {
  BuyBoxSkeleton,
  SupplierRowsSkeleton,
} from "./components/DetailSkeletons";

import useDetailOrigin from "./useDetailOrigin";
import useProductSuppliers from "./useProductSuppliers";
import { INITIAL_PRODUCT_FILTERS, countActiveProductFilters } from "./productSuppliers";
import { deliveryUnavailableSummary } from "./deliverability";
import { formatDistanceKm, formatMoney } from "./detailMappers";
import { CART_PATH, productPath, supplierPath } from "./detailRoutes";

import useCart from "../cart/useCart";
import useAddToCart from "./useAddToCart";
import { GOOGLE_MAP_KEY } from "../../utils/configs";
import { getAuthToken } from "../../lib/api/tokenStore";

import "./styles.css";

const MAP_LIBRARIES = ["places"];
const HERO_HEIGHT = 320;

const ProductDetailRoute = () => {
  const navigate = useNavigate();
  const { productId: routeProductId } = useParams();

  const origin = useDetailOrigin();

  const entryRef = useRef(routeProductId ? Number(routeProductId) : null);
  const entryProductId = entryRef.current;
  const replacedRef = useRef(false);

  const { isLoaded } = useJsApiLoader({
    id: "supplier-google-map",
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries: MAP_LIBRARIES,
  });

  const [filters, setFilters] = useState(INITIAL_PRODUCT_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [sheetSupplier, setSheetSupplier] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const feed = useProductSuppliers({
    entryProductId,
    volume: origin.volume,
    containerOptionId: origin.containerOptionId,
    coordinates: origin.coordinates,
    radiusKm: origin.radiusKm,
    filters,
  });

  useEffect(() => {
    if (entryProductId || !feed.productId || replacedRef.current) return;

    replacedRef.current = true;

    navigate(
      productPath({
        productId: feed.productId,
        volume: origin.volume,
        radiusKm: origin.radiusKm,
        containerOptionId: origin.containerOptionId,
        coordinates: origin.coordinates,
      }),
      { replace: true }
    );
  }, [entryProductId, feed.productId, navigate, origin]);

  const selected = useMemo(() => {
    const { suppliers } = feed;

    return (
      suppliers.find((card) => card.supplierId === selectedSupplierId) ||
      suppliers.find((card) => card.highlighted) ||
      suppliers[0] ||
      null
    );
  }, [feed, selectedSupplierId]);

  const cart = useCart({ enabled: Boolean(getAuthToken()) });

  const { busyAction, addToCart, conflictDialog } = useAddToCart({ cart });

  useEffect(() => {
    setQuantity(1);
  }, [selected?.supplierId]);

  const inCart = selected
    ? cart.contains({
        productId: selected.productId,
        containerMaterialId: null,
        volume: null,
        variableVolume: false,
      })
    : false;

  const focusDistanceKm = selected?.distanceKm ?? feed.suppliers[0]?.distanceKm;

  const summary = useMemo(
    () => deliveryUnavailableSummary(feed.suppliers.map((card) => card.notice)),
    [feed.suppliers]
  );

  const openSupplier = useCallback(
    (card) =>
      navigate(
        supplierPath({
          supplierId: card.supplierId,
          distanceKm: card.distanceKm,
          radiusKm: origin.radiusKm,
          coordinates: origin.coordinates,
        })
      ),
    [navigate, origin]
  );

  const submit = useCallback(
    async (action) => {
      if (!selected) return;

      const result = await addToCart({
        action,
        notice: selected.notice,
        supplierName: selected.name,
        items: [
          {
            productId: selected.productId,
            supplierId: selected.supplierId,
            quantity,
            containerMaterialId: null,
            volume: null,
            variableVolume: false,
          },
        ],
      });

      if (result?.status === "added" && action === "buy") navigate(CART_PATH);
    },
    [selected, quantity, addToCart, navigate]
  );

  const activeFilterCount = countActiveProductFilters(filters, feed.bounds);

  const activeSortId = sortOptionId(filters);

  const applySort = useCallback(
    (id) => {
      const option = SORT_OPTIONS.find((entry) => entry.id === id);
      if (!option) return;

      setFilters((current) => ({
        ...current,
        sort: option.sort,
        minRating: option.minRating,
      }));
    },
    []
  );

  return (
    <DetailScaffold
      title={feed.productTitle}
      heroHeight={HERO_HEIGHT}
      layout="split"
      heroAuto
      hero={
        <Gallery images={feed.productImages} alt={feed.productTitle} showThumbs />
      }
      full={
        <section className="sy-map-section sy-product-info">
          <div className="sy-map-section-head">
            <h2>Supplier locations</h2>

            <p className="sy-distance-row">
              <PinIcon />

              {Number.isFinite(focusDistanceKm)
                ? `${selected ? `${selected.name} · ` : ""}${formatDistanceKm(
                    focusDistanceKm
                  )} from your location`
                : "Distance unavailable"}
            </p>
          </div>

          {isLoaded ? (
            <ProductMapCard
              coordinates={origin.coordinates}
              suppliers={feed.suppliers}
              selectedId={selected?.supplierId ?? null}
              onSelect={(card) => setSelectedSupplierId(card.supplierId)}
            />
          ) : (
            <div className="sy-map-card sy-map-card-empty" aria-hidden="true" />
          )}
        </section>
      }
      actions={
        <button
          type="button"
          className="sy-detail-filter"
          onClick={() => setFiltersOpen(true)}
          aria-label="Filter suppliers"
        >
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="M3 5h14M6 10h8M8.5 15h3" />
          </svg>
          Filters
          {activeFilterCount > 0 && <i>{activeFilterCount}</i>}
        </button>
      }
    >
      {feed.isResolving && !selected ? (
        <BuyBoxSkeleton />
      ) : selected ? (
        <OfferBuyBox
          supplier={selected}
          notice={selected.notice}
          quantity={quantity}
          inCart={inCart}
          busy={cart.isSaving}
          busyAction={busyAction}
          onQuantityChange={setQuantity}
          onAdd={() => submit("add")}
          onBuy={() => submit("buy")}
          onViewCart={() => navigate(CART_PATH)}
          onVisitStore={() => openSupplier(selected)}
        />
      ) : null}

      <section className="sy-product-info sy-suppliers-panel">
        <div className="sy-list-header">
          <h2>Available Suppliers ({feed.total})</h2>
          <p>Pick a supplier to buy from</p>
        </div>

        <TabStrip
          tabs={SORT_OPTIONS}
          activeId={activeSortId}
          label="Sort suppliers"
          variant="radio"
          onSelect={applySort}
        />

        {summary && <p className="sy-list-summary">{summary}</p>}

        {feed.isResolving && !feed.suppliers.length ? (
          <SupplierRowsSkeleton />
        ) : feed.suppliers.length ? (
          <ul
            className="sy-supplier-list"
            role="radiogroup"
            aria-label="Available suppliers"
          >
            {feed.suppliers.map((card) => (
              <SupplierRow
                key={card.key}
                card={card}
                selected={card.supplierId === selected?.supplierId}
                onSelect={(row) => setSelectedSupplierId(row.supplierId)}
                onInfo={setSheetSupplier}
              />
            ))}
          </ul>
        ) : (
          <p className="sy-empty">
            No other supplier nearby is offering this cylinder right now.
          </p>
        )}
      </section>

      <ProductFiltersPanel
        open={filtersOpen}
        filters={filters}
        bounds={feed.bounds}
        asSheet={typeof window !== "undefined" && window.innerWidth < 1024}
        onClose={() => setFiltersOpen(false)}
        onApply={setFilters}
        onReset={() => setFilters(INITIAL_PRODUCT_FILTERS)}
      />

      <SupplierDetailSheet
        open={Boolean(sheetSupplier)}
        supplierId={sheetSupplier?.supplierId}
        supplierName={sheetSupplier?.name}
        initialTab="about"
        priceLabel={
          sheetSupplier
            ? formatMoney(sheetSupplier.price, sheetSupplier.currency)
            : null
        }
        onClose={() => setSheetSupplier(null)}
      />

      {conflictDialog}
    </DetailScaffold>
  );
};

export default ProductDetailRoute;
